import { Box, Button, Chip, IconButton, TextField, Tooltip, Typography } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import React, { useEffect, useRef, useState } from "react";
import { CTM_Colors, IFeedbackData, TimeDBProps } from "../Helper";
import { toast } from "react-toastify";
import pb from "../pocketBase";
import ConfirmDialog from "./Dialog/ConfirmDialog";
import DriverFeedback from "./DriverFeedback";

interface ITimeElementProps {
  id?: string;
  time: string;
  cones: number;
  lapTimeSetAt: Date | null;
  timeComment: string;
  index: number;
  fromDB?: boolean;
}

interface ITimeInputProps {
  runId: string;
}

const TimeInput: React.FC<ITimeInputProps> = ({ runId }) => {
  const timeInputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [isSaved, setIsSaved] = useState(false);
  const [savingIndex, setSavingIndex] = useState<number | null>(null);
  const [times, setTimes] = useState<ITimeElementProps[]>([
    { time: "", cones: 0, index: 0, timeComment: "", lapTimeSetAt: null },
  ]);
  const [editingTimeId, setEditingTimeId] = useState<string | null>(null);
  const [initialFeedback, setInitialFeedback] = useState<IFeedbackData | null>(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [timeToDeleteIndex, setTimeToDeleteIndex] = useState<number | null>(null);

  const parseFeedbackRecord = (record: any): IFeedbackData => {
    const defaults: IFeedbackData = {
      comment: "", grip: 5, limit_Balance: 0,
      control_corner_entry: 5, control_corner_apex: 5, control_corner_exit: 5,
      stability_entry: 5, stability_apex: 5, stability_exit: 5,
      braking_stability: 5, braking_controlability: 5,
      throttle_stability: 5, throttle_controlability: 5,
    };
    return Object.keys(defaults).reduce((acc, key) => ({
      ...acc, [key]: record[key] ?? defaults[key as keyof IFeedbackData]
    }), {} as IFeedbackData);
  };

  useEffect(() => {
    const load = async () => {
      try {
        const records = await pb.collection("Time").getFullList(200, {
          filter: `RunId = "${runId}"`,
          sort: "Index",
          $autoCancel: false,
        });
        if (records.length > 0) {
          setTimes(records.map(r => ({
            id: r.id, time: r.Time, cones: r.Cones,
            lapTimeSetAt: r.LapTimeSetAt, index: r.Index,
            timeComment: r.timeComment, fromDB: true,
          })));
          setIsSaved(true);
          const fb = await pb.collection("DriverFeedback")
            .getFirstListItem(`run = "${runId}"`).catch(() => null);
          if (fb) { setInitialFeedback(parseFeedbackRecord(fb)); setShowFeedback(true); }
        }
      } catch (e) {
        console.error("Error loading times:", e);
      }
    };
    load();
  }, [runId]);

  const validateTimeInput = (v: string) =>
    /^(?:\d{1,3}(?:\.\d{1,3})?|\.\d{1,3})$/.test(v);

  const formatTimeForDB = (time: string): string => {
    if (!time) return "0.000";
    if (time.startsWith(".")) time = `0${time}`;
    if (!time.includes(".")) return `${time}.000`;
    const [s, d] = time.split(".");
    return `${s}.${(d + "000").slice(0, 3)}`;
  };

  const handleDelete = (arrayIndex: number) => {
    const t = times[arrayIndex];
    if (t.id) {
      setTimeToDeleteIndex(arrayIndex);
      setConfirmDialogOpen(true);
    } else {
      let next = times.filter((_, i) => i !== arrayIndex).map((t, i) => ({ ...t, index: i }));
      if (next.length === 0) next = [{ time: "", cones: 0, index: 0, timeComment: "", lapTimeSetAt: null }];
      setTimes(next);
    }
  };

  const confirmDeleteTime = async () => {
    if (timeToDeleteIndex === null) return;
    try {
      await pb.collection("Time").delete(times[timeToDeleteIndex].id!);
      let next = times.filter((_, i) => i !== timeToDeleteIndex).map((t, i) => ({ ...t, index: i }));
      if (next.length === 0) next = [{ time: "", cones: 0, index: 0, timeComment: "", lapTimeSetAt: null }];
      setTimes(next);
      toast.success("Lap time deleted");
    } catch {
      toast.error("Error deleting lap time");
    } finally {
      setConfirmDialogOpen(false);
      setTimeToDeleteIndex(null);
    }
  };

  const saveNewTime = async (arrayIndex: number) => {
    const t = times[arrayIndex];
    if (!t || t.fromDB) return;
    if (!validateTimeInput(t.time)) { toast.error("Fix time format (sss.SSS)"); return; }
    if (savingIndex !== null) return;
    setSavingIndex(arrayIndex);
    try {
      await pb.collection("Time").create({
        Time: formatTimeForDB(t.time), Cones: t.cones,
        timeComment: t.timeComment, lapTimeSetAt: t.lapTimeSetAt || new Date(),
        Index: t.index, RunId: runId,
      } as TimeDBProps & { timeComment: string }, { $autoCancel: false });
      const records = await pb.collection("Time").getFullList(200, {
        filter: `RunId = "${runId}"`, sort: "Index",
      });
      setTimes(records.map(r => ({
        id: r.id, time: r.Time, cones: r.Cones, lapTimeSetAt: r.LapTimeSetAt,
        index: r.Index, timeComment: r.timeComment, fromDB: true,
      })));
      setIsSaved(true);
    } catch {
      toast.error("Error saving time");
    } finally {
      setSavingIndex(null);
    }
  };

  const handleSaveEdit = async (timeId: string | undefined) => {
    if (!timeId) return;
    const t = times.find(t => t.id === timeId);
    if (!t) return;
    if (!validateTimeInput(t.time)) { toast.error("Invalid time format (sss.SSS)"); return; }
    try {
      await pb.collection("Time").update(timeId, {
        Time: formatTimeForDB(t.time), Cones: t.cones,
        timeComment: t.timeComment, lapTimeSetAt: t.lapTimeSetAt || new Date(),
      });
      setEditingTimeId(null);
      toast.success("Time updated");
      const records = await pb.collection("Time").getFullList(200, {
        filter: `RunId = "${runId}"`, sort: "Index", $autoCancel: false,
      });
      setTimes(records.map(r => ({
        id: r.id, time: r.Time, cones: r.Cones, lapTimeSetAt: r.LapTimeSetAt,
        index: r.Index, timeComment: r.timeComment, fromDB: true,
      })));
    } catch {
      toast.error("Error updating time");
    }
  };

  const patchTime = (idOrIndex: string | number, patch: Partial<ITimeElementProps>) => {
    setTimes(prev => prev.map((t, i) => {
      if (typeof idOrIndex === 'string' ? t.id === idOrIndex : i === idOrIndex)
        return { ...t, ...patch };
      return t;
    }));
  };

  const handleKeyDown = (e: React.KeyboardEvent, arrayIndex: number, field: 'time' | 'cones' | 'comment') => {
    if (e.key === 'Enter' && !times[arrayIndex].fromDB) {
      e.preventDefault();
      saveNewTime(arrayIndex).then(() => {
        // after save, add a new empty row and focus it
        setTimes(prev => {
          if (prev.some(t => !t.fromDB)) return prev; // already have unsaved row
          return [...prev, { time: "", cones: 0, lapTimeSetAt: null, index: prev.length, timeComment: "" }];
        });
        setTimeout(() => timeInputRefs.current[arrayIndex + 1]?.focus(), 80);
      });
    }
    if (e.key === 'c' && field !== 'comment') {
      e.preventDefault();
      patchTime(arrayIndex, { cones: (times[arrayIndex].cones || 0) + 1 });
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      timeInputRefs.current[arrayIndex + 1]?.focus();
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      timeInputRefs.current[arrayIndex - 1]?.focus();
    }
  };

  const bestTime = times.filter(t => t.fromDB && t.time).reduce<string | null>((best, t) => {
    if (!best) return t.time;
    return parseFloat(t.time) < parseFloat(best) ? t.time : best;
  }, null);

  return (
    <Box>
      {/* Header row */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography color={CTM_Colors.ctm_green} variant="h6">Times</Typography>
        {bestTime && (
          <Chip
            label={`Best: ${bestTime}`}
            size="small"
            sx={{ fontFamily: 'monospace', fontSize: '0.75rem',
              backgroundColor: CTM_Colors.ctm_green, color: '#fff' }}
          />
        )}
      </Box>

      {/* Rows */}
      <Box sx={{
        border: `1px solid ${CTM_Colors.ctm_green}60`,
        borderRadius: 1,
        overflow: 'hidden',
      }}>
        {/* Column headers */}
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: '2rem 1fr 1fr 1fr 72px',
          gap: '0 16px',
          px: 1,
          py: 0.5,
          alignItems: 'center',
          borderBottom: `1px solid ${CTM_Colors.ctm_green}60`,
        }}>
          <Typography variant="caption" color="text.secondary">#</Typography>
          <Typography variant="caption" color="text.secondary">Time (s)</Typography>
          <Typography variant="caption" color="text.secondary">Cones</Typography>
          <Typography variant="caption" color="text.secondary">Note</Typography>
          <Box />
        </Box>
        {times.map(({ id, time, cones, timeComment, fromDB, index }, arrayIndex) => {
          const isEditing = editingTimeId === id;
          const isNew = !fromDB;
          const isActive = isEditing || isNew;
          const isBest = fromDB && !!bestTime && time === bestTime;

          return (
            <Box
              key={id ?? arrayIndex}
              sx={theme => ({
                display: 'grid',
                gridTemplateColumns: '2rem 1fr 1fr 1fr 72px',
                gap: '0 16px',
                alignItems: 'center',
                px: 1,
                py: isActive ? 0.75 : 0.5,
                borderBottom: arrayIndex < times.length - 1 ? `1px solid ${theme.palette.divider}` : 'none',
                backgroundColor: isActive
                  ? `${CTM_Colors.ctm_green}15`
                  : isBest
                    ? `${CTM_Colors.ctm_green}10`
                    : 'transparent',
                transition: 'background-color 0.15s',
                '&:hover .row-actions': { opacity: 1 },
              })}
            >
              {/* Lap number */}
              <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                {index + 1}
              </Typography>

              {/* Time field */}
              {isActive ? (
                <TextField
                  inputRef={el => (timeInputRefs.current[arrayIndex] = el)}
                  value={time}
                  onChange={e => patchTime(isNew ? arrayIndex : id!, { time: e.target.value })}
                  onKeyDown={e => handleKeyDown(e, arrayIndex, 'time')}
                  size="small"
                  placeholder="sss.SSS"
                  autoFocus={isNew}
                  error={time !== '' && !validateTimeInput(time)}
                  inputProps={{ style: { fontFamily: 'monospace', fontSize: '0.85rem', padding: '4px 8px' } }}
                  sx={{ '& .MuiOutlinedInput-root': { height: 32 } }}
                />
              ) : (
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: 'monospace', fontWeight: isBest ? 700 : 400,
                    color: isBest ? CTM_Colors.ctm_green : 'text.primary',
                    fontSize: '0.9rem',
                  }}
                >
                  {time}
                  {isBest && <Typography component="span" variant="caption" sx={{ ml: 0.5, color: CTM_Colors.ctm_green }}>★</Typography>}
                </Typography>
              )}

              {/* Cones */}
              {isActive ? (
                <TextField
                  value={cones}
                  type="number"
                  onChange={e => patchTime(isNew ? arrayIndex : id!, { cones: e.target.value === '' ? 0 : parseInt(e.target.value) })}
                  onKeyDown={e => handleKeyDown(e, arrayIndex, 'cones')}
                  size="small"
                  inputProps={{ min: 0, style: { fontFamily: 'monospace', fontSize: '0.85rem', padding: '4px 6px' } }}
                  sx={{ '& .MuiOutlinedInput-root': { height: 32 } }}
                />
              ) : (
                cones > 0
                  ? <Chip label={`+${cones}`} size="small" color="error" sx={{ height: 20, fontSize: '0.7rem', fontWeight: 700, width: 'fit-content' }} />
                  : <Typography variant="body2" color="text.disabled" sx={{ fontFamily: 'monospace' }}>—</Typography>
              )}

              {/* Comment */}
              {isActive ? (
                <TextField
                  value={timeComment}
                  onChange={e => patchTime(isNew ? arrayIndex : id!, { timeComment: e.target.value })}
                  onKeyDown={e => handleKeyDown(e, arrayIndex, 'comment')}
                  size="small"
                  placeholder="note…"
                  inputProps={{ style: { fontSize: '0.8rem', padding: '4px 8px' } }}
                  sx={{ '& .MuiOutlinedInput-root': { height: 32 } }}
                />
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem', fontStyle: timeComment ? 'normal' : 'italic', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {timeComment || ''}
                </Typography>
              )}

              {/* Actions */}
              <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', minWidth: 72, justifyContent: 'flex-end' }}>
                {isEditing ? (
                  <>
                    <Tooltip title="Save">
                      <IconButton size="small" onClick={() => handleSaveEdit(id)} sx={{ color: CTM_Colors.ctm_green, p: 0.5 }}>
                        <CheckIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Cancel">
                      <IconButton size="small" onClick={() => setEditingTimeId(null)} sx={{ p: 0.5 }}>
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </>
                ) : fromDB ? (
                  <Box className="row-actions" sx={{ display: 'flex', gap: 0.5, opacity: 0, transition: 'opacity 0.15s' }}>
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => setEditingTimeId(id!)} disabled={editingTimeId !== null} sx={{ p: 0.5, color: CTM_Colors.ctm_green }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" onClick={() => handleDelete(arrayIndex)} disabled={editingTimeId !== null} sx={{ p: 0.5, color: 'error.main' }}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                ) : (
                  <>
                    <Tooltip title="Save">
                      <IconButton size="small" onClick={() => saveNewTime(arrayIndex)} disabled={savingIndex !== null} sx={{ color: CTM_Colors.ctm_green, p: 0.5 }}>
                        <CheckIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Discard">
                      <IconButton size="small" onClick={() => handleDelete(arrayIndex)} sx={{ p: 0.5 }}>
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>

      {/* Add row button */}
      {!editingTimeId && (
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={() => setTimes(prev => [...prev, { time: "", cones: 0, lapTimeSetAt: null, index: prev.length, timeComment: "" }])}
          sx={{ mt: 1, color: CTM_Colors.ctm_green }}
        >
          Add lap
        </Button>
      )}

      {/* Driver feedback */}
      {(isSaved || showFeedback) && (
        <Box sx={{ mt: 3 }}>
          <DriverFeedback runId={runId} initialFeedback={initialFeedback} />
        </Box>
      )}

      <ConfirmDialog
        open={confirmDialogOpen}
        title="Delete Lap Time"
        message="Are you sure you want to delete this lap time?"
        onConfirm={confirmDeleteTime}
        onCancel={() => setConfirmDialogOpen(false)}
        confirmText="Delete"
        cancelText="Cancel"
        confirmColor="error"
      />
    </Box>
  );
};

export default TimeInput;
